import {
  calculateDistanceBetweenPositions,
  CoreApi,
  ProductType
} from '@inter-ikea-storageone/range';
import {
  ComponentIds,
  Connection,
  Connector,
  ConnectorsComponent,
  Entity,
  GuestConnectionsComponent
} from '@inter-ikea-core/core';

type ConnectorWithHost = Connector & { host: Entity };

export const getCurrentConnectionsOfType = (coreApi: CoreApi, type: ProductType) =>
  coreApi.entities
    .getByProductType(type)
    .flatMap(
      entity =>
        entity.getComponent<GuestConnectionsComponent>(ComponentIds.GUEST_CONNECTIONS_COMPONENT_ID)
          ?.connections
    )
    .filter(Boolean) as Connection[];

export const getAllConnectorsThatOffer = (coreApi: CoreApi, offersKey: string) => {
  const entitiesWithConnectors = coreApi.entities.getByComponent(
    ComponentIds.CONNECTORS_COMPONENT_ID
  );

  return entitiesWithConnectors.flatMap(entity => getEntityConnectorsThatOffer(entity, offersKey));
};

export const getEntityConnectorsThatOffer = (
  entity: Entity,
  offersKey: string
): ConnectorWithHost[] => {
  const connectors = entity
    .getComponent<ConnectorsComponent>(ComponentIds.CONNECTORS_COMPONENT_ID)
    ?.connectors.filter(c => c.isOffering(offersKey));

  return connectors ? connectors.map(c => Object.assign(c, { host: entity })) : [];
};

export const getConnectorScreenPosition = (coreApi: CoreApi, connector: ConnectorWithHost) => {
  const coord3D = connector.getWorldPositionFromOwnerEntity(connector.host);
  const coord2D = coreApi
    .getModules()
    .viewer()!
    .worldCoordToCanvas(coord3D.X, coord3D.Y, coord3D.Z);

  if (!coord2D) return null;

  return { x: coord2D.X, y: coord2D.Y };
};

export const getClosestConnectorWithKey = (
  coreApi: CoreApi,
  offersKey: string,
  position: { x: number; y: number }
) => {
  const allConnectors = getAllConnectorsThatOffer(coreApi, offersKey);
  const allConnectorsWithDistance = allConnectors
    .map(c => {
      const cPos = getConnectorScreenPosition(coreApi, c);

      if (!cPos) return null;

      const distance = calculateDistanceBetweenPositions(position, cPos);
      return Object.assign(c, { distance });
    })
    .filter(Boolean) as (typeof allConnectors[number] & { distance: number })[];

  const [targetConnector] = allConnectorsWithDistance.sort((a, b) => a.distance - b.distance);

  return (targetConnector ?? null) as typeof targetConnector | null;
};
